import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ILogger,
  MediaItem,
  MediaPickerProps,
  MediaType,
  PickerMode,
  getAcceptedFormatsForMediaTypes,
  getFileDataFromUrl,
  getMediaTypeByExt,
  useGrippContext,
  useUploadCounter,
  validateMediaFile,
} from '@gripp/shared-logic';
import { Colors, useSystemNotification } from '@gripp/shared-ui';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';
import { Card } from 'react-native-paper';
import { useMediaUpload } from '../../hooks/useMediaUpload';

type MediaPickerExtendedProps = MediaPickerProps & {
  autoUpload?: boolean;
  onFileSelected?: (file: File) => void;
  name?: string;
  categories?: Array<{ id: string }>;
  autoTrigger?: boolean;
  /**
   * Delay (ms) before the auto-triggered picker opens. Defaults to 100ms.
   * Callers rendered inside a sliding panel should pass a value larger than
   * the panel transition so the OS dialog opens over the settled panel.
   */
  autoTriggerDelay?: number;
};

export const MediaPicker = (props: MediaPickerExtendedProps) => {
  const {
    acceptedMediaTypes = [MediaType.Image],
    autoUpload = true,
    onFileSelected,
    workspaceId,
    name,
    categories,
    autoTrigger = false,
    autoTriggerDelay,
  } = props;
  const { logger } = useGrippContext();
  const { t } = useTranslation();
  const { showError } = useSystemNotification();
  let icon;
  if (props.icon) {
    icon = props.icon;
  } else {
    icon = props.mode === PickerMode.CAMERA ? 'camera' : 'image-plus';
  }
  const fileUploadTriggerTimeout = 100;
  const iconSize = props.iconSize || 24;
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const { startUpload, endUpload } = useUploadCounter(props.setUploading);

  const uploadMedia = useMediaUpload({
    onProgress: (mediaId: string, progress: number) => {
      props.setProgress(mediaId, progress);
    },
    onComplete: (_mediaId: string) => {
      endUpload();
    },
    onError: (_error: unknown, mediaId: string) => {
      props.onUploadError?.(mediaId);
      showError(t('issue.footer.attachmentFailed'));
      endUpload();
    },
    onStart: (mediaItem: MediaItem) => {
      props.setMedia({
        ...mediaItem,
        isUploading: true,
        uploadProgress: 0,
      });
    },
  });
  const captureProps: any = {};
  if (props.mode === PickerMode.CAMERA) {
    captureProps.capture = 'environment';
  }

  const acceptedFormats = getAcceptedFormatsForMediaTypes(acceptedMediaTypes);

  // Auto-trigger file picker on mount if autoTrigger is true
  useEffect(() => {
    if (autoTrigger && fileUploadRef.current) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        fileUploadRef.current?.click();
      }, autoTriggerDelay ?? fileUploadTriggerTimeout);
      return () => clearTimeout(timer);
    }
  }, [autoTrigger, autoTriggerDelay]);

  // Expose openPicker callback for programmatic triggering
  useEffect(() => {
    if (props.onPickerReady) {
      props.onPickerReady(() => {
        fileUploadRef.current?.click();
      });
    }
  }, []);

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    const file = e.target.files[0];
    // Reset the input value immediately so re-picking the same file (after a
    // remove, a rejection, or any other dismissal) fires onChange again.
    e.target.value = '';
    const claimedExt = checkAcceptedExtension(
      file.name,
      acceptedMediaTypes,
      logger
    );
    if (!claimedExt) {
      return;
    }

    const headerBytes = new Uint8Array(await file.slice(0, 32).arrayBuffer());
    const result = validateMediaFile(headerBytes, claimedExt);

    if (!result.ok) {
      logger?.warning(
        `Media picker web - validation failed: reason=${result.reason} sniffed=${
          result.sniffed?.ext ?? 'null'
        } claimed=${claimedExt}`
      );
      showError(t('media.validation.unsupported'));
      return;
    }

    let overrideExtension: string | undefined;
    if (result.sniffed && result.correctedExt) {
      overrideExtension = result.correctedExt;
      logger?.info(
        `Media picker web - corrected extension: claimed=${claimedExt} sniffed=${result.sniffed.ext}`
      );
    }

    if (autoUpload) {
      startUpload();
      try {
        const mediaId = await uploadMedia(
          file,
          workspaceId,
          name,
          categories,
          overrideExtension
        );
        if (!mediaId) {
          endUpload();
        }
      } catch {
        endUpload();
      }
    } else if (onFileSelected) {
      if (overrideExtension && result.sniffed) {
        const baseName = file.name.replace(/\.[^.]+$/, '');
        const correctedFile = new File(
          [file],
          `${baseName}.${overrideExtension}`,
          { type: result.sniffed.mime, lastModified: file.lastModified }
        );
        onFileSelected(correctedFile);
      } else {
        onFileSelected(file);
      }
    }
  };

  const openFilePicker = () => fileUploadRef.current?.click();

  return (
    <>
      <input
        ref={fileUploadRef}
        type="file"
        style={{ display: 'none' }}
        accept={acceptedFormats}
        onChange={handleFileInputChange}
        multiple={false}
        {...captureProps}
      />
      {props.buttonComponent ? (
        React.cloneElement(props.buttonComponent, {
          ...props.buttonComponent.props,
          onPress: openFilePicker,
        })
      ) : props.isIconInBox ? (
        <MaterialCommunityIcons
          name={(props.icon as any) || 'camera-outline'}
          color={props.iconColor || Colors.white}
          size={props.iconSize || 16}
          onPress={openFilePicker}
        />
      ) : (
        <Pressable onPress={openFilePicker}>
          {props?.customTrigger ? (
            props.customTrigger
          ) : (
            <Card
              mode={props.cardStyle || undefined}
              style={props.style || styles.cardIcon}
            >
              <MaterialCommunityIcons
                name={icon as any}
                color={props.iconColor || Colors.role.textStrong}
                size={iconSize}
              />
            </Card>
          )}
        </Pressable>
      )}
    </>
  );
};

const checkAcceptedExtension = (
  fileName: string,
  acceptedMediaTypes: MediaType[],
  logger?: ILogger
): string | null => {
  try {
    const ext = getFileDataFromUrl(fileName).ext;
    const fileMediaType = getMediaTypeByExt(ext);
    if (!acceptedMediaTypes.includes(fileMediaType)) {
      logger?.warning(
        `Media picker web - format does not match accepted types: ${fileMediaType}`
      );
      return null;
    }
    return ext;
  } catch (e) {
    logger?.warning(`Media picker web - failed to check file format: ${e}`);
    return null;
  }
};

const styles = StyleSheet.create({
  imageCard: {
    height: 100,
    width: 100,
  },
  imageCardContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 20,
  },
  cardIcon: {
    height: 45,
    width: 45,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginTop: -30,
    marginRight: -20,
  },
  progress: {
    marginTop: 5,
  },
});
